"use client";

import OrnamentSVG from "@/public/assets/images/ornament.svg";
import Text from "../Text";
import GradientLightText from "../GradientLightText";
import { CourseType, CourseTypeType } from "@/types/Courses";
import { useTranslations } from "next-intl";
import Button from "../Button";
import { ProductStatusType } from "@/types/Products";

type DiscountType = {
  value: string;
  text: string;
  regular: string;
  discount: string;
};

type InstallmentType = {
  monthlyText: string;
  text: string;
};

type ButtonType = {
  text: string;
  link: string;
};

export type ComponentType = {
  type: string;
  content: {
    offerCounterText: string;
    discount?: DiscountType;
    button: ButtonType;
    installments: InstallmentType;
  };
  course?: CourseType;
};

type PriceBoxPricesProps = {
  hasDiscount?: boolean;
  regularText?: string;
  discountText?: string;
  price: number;
  price_rub?: number;
  withoutDiscount: number;
  withoutDiscountRub?: number;
  discount?: number;
};

type InstallmentsProps = {
  hasDiscount?: boolean;
  price: number;
  price_rub?: number;
  withoutDiscount: number;
  withoutDiscountRub?: number;
  discount?: number;
  content: InstallmentType;
};

type PriceBoxProps = {
  component: ComponentType;
};

function PriceBox({ component }: PriceBoxProps) {
  const t = useTranslations("common");

  const discount = 50;

  const proPrice =
    component.course?.products?.[0].price_eur! +
    component.course?.products?.[0].group_lesson_price_eur! *
      component.course?.group_lessons_count! +
    component.course?.products?.[0].exam_price_eur!;
  const proPrice_rub =
    component.course?.products?.[0].price_rub! +
    component.course?.products?.[0].group_lesson_price_rub! *
      component.course?.group_lessons_count! +
    component.course?.products?.[0].exam_price_rub!;

  const basicPartDiscount =
    component.course?.products?.[0].price_eur! / (1 - discount! / 100) +
    component.course?.products?.[0].group_lesson_price_eur! *
      component.course?.group_lessons_count! +
    component.course?.products?.[0].exam_price_eur!;

  const proDiscount = Number(proPrice / (1 - discount! / 100));
  const proDiscountRub = Number(proPrice_rub / (1 - discount! / 100));

  return (
    <div
      className={`price-box ${
        component.course?.tags?.includes("discount")
          ? "price-box--discount"
          : ""
      }`.trim()}
    >
      {component.course?.tags?.includes("discount") ? (
        <div className="price-box__discount-badge">
          <p className="price-box__discount-badge-number weight-700">
            {component.content.discount?.value}%
          </p>
          <p className="price-box__discount-badge-text weight-700">
            {component.content.discount?.text}s
          </p>
        </div>
      ) : null}
      <div className="price-box__holder">
        <div className="price-box__content">
          <div className="price-box__ornament">
            <OrnamentSVG />
          </div>
          <Text
            element="h3"
            elementStyle="h2-secondary"
            fontFamily="noto-serif"
            className="weight-700 price-box__title"
          >
            <GradientLightText element="span">
              {component.course?.title?.split(":")[0]}
            </GradientLightText>
          </Text>
          <Text element="p" elementStyle="subtitle-secondary">
            <GradientLightText element="span">
              {t("proCourse")}
            </GradientLightText>
          </Text>
          <PriceBoxPrices
            hasDiscount={component.course?.tags?.includes("discount")}
            discount={Number(component.content.discount?.value)}
            regularText={component.content.discount?.regular}
            discountText={component.content.discount?.discount}
            withoutDiscount={proPrice}
            withoutDiscountRub={proPrice_rub}
            price={
              component.course?.tags?.includes("discount")
                ? proDiscount
                : proPrice
            }
            price_rub={
              component.course?.tags?.includes("discount")
                ? proDiscountRub
                : proPrice_rub
            }
          />
          {component.course?.products?.[0].status ===
          ProductStatusType.Active ? (
            <Button
              type="primary"
              isLink
              isTargetBlank
              event={{
                event: "add_to_cart",
                value: component.course?.title,
                label: "Item added to cart",
                category: "website",
              }}
              href={
                process.env.NEXT_PUBLIC_PORTAL_URL +
                `/portal/account/basket?product={"type":"pro","product_id":${
                  component.course?.products?.[0].id
                },"course_id":${
                  component.course?.id
                },"price":${proPrice},"price_rub":${proPrice_rub},"isDiscount":${!!component.course?.tags?.includes(
                  "discount"
                )}}`
              }
            >
              {component.content.button.text}
            </Button>
          ) : null}
          <Installments
            hasDiscount={component.course?.tags?.includes("discount")}
            discount={Number(component.content.discount?.value)}
            withoutDiscount={proPrice}
            withoutDiscountRub={proPrice_rub}
            price={
              component.course?.tags?.includes("discount")
                ? proDiscount
                : proPrice
            }
            price_rub={
              component.course?.tags?.includes("discount")
                ? proDiscountRub
                : proPrice_rub
            }
            content={component.content.installments}
          />
        </div>
      </div>
    </div>
  );
}

const PriceBoxPrices = ({
  hasDiscount,
  regularText,
  discountText,
  withoutDiscount,
  price,
  price_rub,
  withoutDiscountRub,
  discount,
}: PriceBoxPricesProps) => {
  const calculatedPrice = price.toFixed();
  const calculatedPriceRub = price_rub?.toFixed();
  return (
    <div className="price-box__price-holder">
      {!hasDiscount ? (
        <>
          <p className="price-box__price weight-700 noto-serif">
            <GradientLightText element="span">
              {calculatedPrice} €
            </GradientLightText>
            {calculatedPriceRub ? (
              <GradientLightText element="p">
                {calculatedPriceRub} ₽
              </GradientLightText>
            ) : null}
          </p>
        </>
      ) : (
        <>
          <p className="price-box__price weight-700 noto-serif">
            {calculatedPrice} €
            {calculatedPriceRub ? (
              <p style={{ fontSize: "22px", lineHeight: "22px" }}>
                {calculatedPriceRub} ₽
              </p>
            ) : null}
            <span className="price-box__price-text">{regularText}</span>
          </p>
          <span>{">"}</span>
          <p className="price-box__price weight-700 noto-serif">
            <GradientLightText element="span">
              {withoutDiscount.toFixed()} €
            </GradientLightText>
            {withoutDiscountRub ? (
              <p
                className="light-gradient"
                style={{ fontSize: "22px", lineHeight: "22px" }}
              >
                {withoutDiscountRub.toFixed()} ₽
              </p>
            ) : null}
            <span className="price-box__price-text">
              <GradientLightText element="span">
                {discountText}
              </GradientLightText>
            </span>
          </p>
        </>
      )}
    </div>
  );
};

const Installments = ({
  price,
  discount,
  hasDiscount,
  price_rub,
  withoutDiscountRub,
  withoutDiscount,
  content,
}: InstallmentsProps) => {
  const currentPrice: number = !hasDiscount ? price : withoutDiscount;
  const currentPriceRub = !hasDiscount ? price_rub : withoutDiscountRub;

  return (
    <div className="price-box__installments">
      <Text
        element="p"
        elementStyle="subtitle-secondary"
        fontFamily="noto-serif"
        className="weight-700 price-box__installments-text"
      >
        {`${(currentPrice / 24).toFixed(2)}€${
          currentPriceRub ? `/${(currentPriceRub / 24).toFixed(2)}₽` : null
        } ${content.monthlyText}`}
      </Text>
      <Text
        element="p"
        elementStyle="body-secondary"
        fontFamily="noto-serif"
        className="weight-700 price-box__installments-text"
      >
        {content.text}
      </Text>
    </div>
  );
};

export default PriceBox;
